<template>
    <div class="d-block">
        <!-- ACTIONS LIST -->
        <transition name="fade">
            <div v-if="!hazards" class="d-block">
                <h4 class="mb-2">Actions</h4>
                <template v-if="actions.length < 1">No actions.</template>
                <div
                    v-for="(action, index) in actions"
                    class="d-flex justify-content-between border mb-4 p-2 rounded"
                    :key="action.id"
                >
                    <div class="d-flex row">
                        <span>{{ action.description }}</span>
                        <small>
                            {{ action.hazards?.length }}
                            {{ action.hazards?.length === 1 ? "hazard" : "hazards" }}
                        </small>
                    </div>
                    <div class="d-flex">
                        <button class="btn btn-link" v-if="editable" @click="$emit('remove-action', index)">
                            <!-- X icon -->
                            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" class="bi bi-x" viewBox="0 0 16 16">
                                <path d="M4.646 4.646a.5.5 0 0 1 .708 0L8 7.293l2.646-2.647a.5.5 0 0 1 .708.708L8.707 8l2.647 2.646a.5.5 0 0 1-.708.708L8 8.707l-2.646 2.647a.5.5 0 0 1-.708-.708L7.293 8 4.646 5.354a.5.5 0 0 1 0-.708"/>
                            </svg>
                        </button>
                        <button class="btn btn-link" @click="openHazards(action, index)">
                            <!-- Open icon -->
                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" width="18" height="18">
                                <path fill-rule="evenodd" d="M15.28 9.47a.75.75 0 0 1 0 1.06l-4.25 4.25a.75.75 0 1 1-1.06-1.06L13.69 10 9.97 6.28a.75.75 0 0 1 1.06-1.06l4.25 4.25ZM6.03 5.22l4.25 4.25a.75.75 0 0 1 0 1.06l-4.25 4.25a.75.75 0 0 1-1.06-1.06L8.69 10 4.97 6.28a.75.75 0 0 1 1.06-1.06Z" clip-rule="evenodd"/>
                            </svg>
                            <span class="sr-only">Open Action</span>
                        </button>
                    </div>
                </div>
            </div>
        </transition>

        <!-- HAZARDS + CONTROLS -->
        <transition name="fade">
            <div v-if="hazards" class="d-block">
                <button class="btn btn-sm mb-3" @click="() => { hazards = null; controls = null; controlsOpen = false; hazardActionIndex = null; }">
                    <!-- Back button -->
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" width="18" height="18">
                        <path fill-rule="evenodd" d="M11.78 5.22a.75.75 0 0 1 0 1.06L8.06 10l3.72 3.72a.75.75 0 1 1-1.06 1.06l-4.25-4.25a.75.75 0 0 1 0-1.06l4.25-4.25a.75.75 0 0 1 1.06 0Z" clip-rule="evenodd"/>
                    </svg>
                    All Actions
                </button>

                <!-- Hazards -->
                <div v-for="(hazard, index) in hazards" :key="hazard.id" class="d-flex row justify-content-between border mb-4 p-2 rounded">
                    <div class="d-flex justify-content-between w-100">
                        <div class="d-flex row flex-grow-1">
                            <small class="font-weight-bold">{{ hazard.description }}</small>
                            <small>
                                {{ hazard.controls?.length }}
                                {{ hazard.controls?.length === 1 ? "control" : "controls" }}
                            </small>
                        </div>
                        <div class="d-flex align-items-start">
                            <button class="btn btn-link" v-if="editable" @click="$emit('remove-hazard', hazardActionIndex, index)">
                                <!-- X icon -->
                                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" class="bi bi-x" viewBox="0 0 16 16">
                                    <path d="M4.646 4.646a.5.5 0 0 1 .708 0L8 7.293l2.646-2.647a.5.5 0 0 1 .708.708L8.707 8l2.647 2.646a.5.5 0 0 1-.708.708L8 8.707l-2.646 2.647a.5.5 0 0 1-.708-.708L7.293 8 4.646 5.354a.5.5 0 0 1 0-.708"/>
                                </svg>
                            </button>
                            <link-marker v-slot="{active}" entity="hazard" :identifier="hazard.id" @updated="marker => hazard.marker = marker">
                                <button class="btn btn-sm m-2" >
                                    <img v-if="hazard?.marker" :src="hazard?.marker" alt=""width="24" :class="{'border': active}">
                                </button>
                            </link-marker>
                            <button class="btn btn-link" @click="openControls(hazard)">
                                <!-- Arrow icon -->
                                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" width="18" height="18">
                                    <path fill-rule="evenodd" d="M8.22 5.22a.75.75 0 0 1 1.06 0l4.25 4.25a.75.75 0 0 1 0 1.06l-4.25 4.25a.75.75 0 0 1-1.06-1.06L11.94 10 8.22 6.28a.75.75 0 0 1 0-1.06Z" clip-rule="evenodd"/>
                                </svg>
                            </button>
                        </div>
                    </div>

                    <!-- Controls (nested) -->
                    <transition name="fade">
                        <div class="mt-3 w-100" v-if="controlsOpen === hazard.id">
                            <div v-for="(control, cIndex) in controls" :key="control.id" class="d-flex justify-content-between border border-light m-3 mb-4 p-2 rounded">
                                <span>{{ control.description }}</span>
                                <link-marker v-slot="{active}" entity="control" :identifier="control?.id" @updated="marker => control.marker = marker">
                                    <button class="btn btn-sm m-2">
                                        <img v-if="control?.marker" :src="control?.marker" alt="" width="24" :class="{'border': active}">
                                    </button>
                                </link-marker>
                                <button
                                    class="btn btn-link"
                                    v-if="editable"
                                    @click="$emit('remove-control', hazardActionIndex, cIndex, index)"
                                >
                                    <!-- X icon -->
                                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" class="bi bi-x" viewBox="0 0 16 16">
                                        <path d="M4.646 4.646a.5.5 0 0 1 .708 0L8 7.293l2.646-2.647a.5.5 0 0 1 .708.708L8.707 8l2.647 2.646a.5.5 0 0 1-.708.708L8 8.707l-2.646 2.647a.5.5 0 0 1-.708-.708L7.293 8 4.646 5.354a.5.5 0 0 1 0-.708"/>
                                    </svg>
                                </button>
                            </div>
                        </div>
                    </transition>
                </div>
            </div>
        </transition>
    </div>
</template>

<script>
import LinkMarker from './LinkMarker'
export default {
    components: {
        LinkMarker,
    },
    props: {
        actions: Array,
        editable: Boolean
    },
    data() {
        return {
            hazards: null,
            hazardActionIndex: null,
            controlsOpen: false,
            controls: null
        };
    },
    methods: {
        openHazards(action, index) {
            this.hazards = action.hazards || [];
            this.hazardActionIndex = index;
            this.controlsOpen = false;
            this.controls = null;
        },
        openControls(hazard) {
            if (this.controlsOpen === hazard.id) {
                this.controlsOpen = false;
                this.controls = null;
            } else {
                this.controlsOpen = hazard.id;
                this.controls = hazard.controls || [];
            }
        }
    }
};
</script>

<style scoped lang="scss">
.fade-enter-active,
.fade-leave-active {
    transition: opacity 0.1s;
}
.fade-enter,
.fade-leave-to {
    opacity: 0;
}
</style>
